// Clan Wars dialog — create / join / leave / view members / chat.
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { Users, Crown, LogOut, Plus, Send } from "lucide-react";
import { getEmail } from "@/lib/pro";
import { setCachedClan, clearCachedClan } from "@/lib/clan";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GEM_COLORS = {
  crown: "#FFD400",
  fire: "#FF6030",
  star: "#F0ABFC",
  prismatic: "#B0F0FF",
};

export default function ClanDialog({ open, onOpenChange, onChanged }) {
  const [me, setMe] = useState(null); // null=loading, {in_clan:false}, or full
  const [openClans, setOpenClans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [tag, setTag] = useState("");

  const email = getEmail();

  const refresh = useCallback(() => {
    if (!open || !email) return;
    axios
      .get(`${API}/clan/me`, { params: { email } })
      .then((r) => {
        setMe(r.data);
        if (r.data?.in_clan) {
          setCachedClan({
            clan_id: r.data.clan_id,
            name: r.data.name,
            tag: r.data.tag,
          });
        } else {
          clearCachedClan();
        }
      })
      .catch(() => setMe({ in_clan: false }));
    axios
      .get(`${API}/clan/list`, { params: { limit: 12 } })
      .then((r) => setOpenClans(r.data?.rows || []))
      .catch(() => setOpenClans([]));
  }, [open, email]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const create = async () => {
    if (!email) {
      toast.error("SET AN EMAIL FIRST · buy any pass or coupon");
      return;
    }
    const cleanName = name.trim();
    const cleanTag = tag.trim().toUpperCase();
    if (cleanName.length < 3 || cleanTag.length < 2) {
      toast.error("Enter a name (3-20) and tag (2-5)");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/clan/create`, {
        email,
        name: cleanName,
        tag: cleanTag,
      });
      toast.success(`CLAN [${r.data.tag}] ${r.data.name} CREATED`);
      setName("");
      setTag("");
      refresh();
      onChanged && onChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Create failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const join = async (clan_id, label) => {
    if (!email) {
      toast.error("SET AN EMAIL FIRST");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/clan/join`, { email, clan_id });
      toast.success(`JOINED ${label}`);
      refresh();
      onChanged && onChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Join failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const leave = async () => {
    if (!email || !window.confirm("Leave this clan? Weekly contribution stays logged.")) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/clan/leave`, { email });
      toast.success(r.data?.disbanded ? "CLAN DISBANDED" : "LEFT CLAN");
      clearCachedClan();
      refresh();
      onChanged && onChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Leave failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="clan-dialog"
        className="max-w-2xl bg-black/95 backdrop-blur-xl border border-[#F0ABFC]/40 text-white"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 tracking-[0.3em] text-[#F0ABFC]">
            <Users size={16} /> CLAN WARS
          </DialogTitle>
          <DialogDescription className="text-white/50 text-xs tracking-[0.2em]">
            Team up · every chip you earn feeds your clan's weekly score
          </DialogDescription>
        </DialogHeader>

        {!email && (
          <div className="text-xs text-white/60 border border-yellow-500/40 bg-yellow-500/5 p-3 tracking-wider">
            Set an account email first (buy any pass or apply a coupon) — clans track chip contributions per player.
          </div>
        )}

        {me === null && email && (
          <div className="text-xs text-white/50 tracking-[0.3em] py-6 text-center">LOADING…</div>
        )}

        {me && me.in_clan && (
          <div data-testid="clan-current" className="space-y-4">
            <div className="border border-[#F0ABFC]/40 p-4 bg-[#F0ABFC]/5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span
                    data-testid="clan-tag"
                    className="text-[10px] tracking-[0.4em] px-2 py-1 border border-[#F0ABFC] text-[#F0ABFC] bg-black/40"
                  >
                    [{me.tag}]
                  </span>
                  <span data-testid="clan-name" className="text-sm text-white tracking-wider font-semibold">
                    {me.name}
                  </span>
                </div>
                <div className="text-[10px] tracking-[0.3em] text-white/50">
                  {me.member_count}/{me.member_max} · WEEK OF {me.week_start}
                </div>
              </div>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-[10px] tracking-[0.4em] text-white/50">TOTAL WEEK</span>
                <span
                  data-testid="clan-total-week"
                  className="text-2xl font-bold text-[#39FF14] tabular-nums"
                  style={{ textShadow: "0 0 12px rgba(57,255,20,0.4)" }}
                >
                  {me.total_week}
                </span>
                <span className="text-[10px] text-white/50">CHIPS</span>
              </div>
              <ol className="space-y-1.5">
                {(me.members || []).map((m, i) => (
                  <li
                    key={m.handle + i}
                    data-testid={`clan-member-${i}`}
                    className="flex items-center justify-between text-xs font-mono border-b border-white/5 pb-1.5"
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-5 text-right text-white/40 tabular-nums">{i + 1}</span>
                      {m.is_leader && (
                        <Crown size={11} className="text-[#FFD400]" data-testid={`clan-leader-crown-${i}`} />
                      )}
                      <span
                        className="text-white/80"
                        style={m.gem && m.gem !== "prismatic" ? { color: GEM_COLORS[m.gem], textShadow: `0 0 6px ${GEM_COLORS[m.gem]}88` } : {}}
                      >
                        {m.handle}
                      </span>
                    </div>
                    <span className="tabular-nums text-white/70">{m.chips_week}</span>
                  </li>
                ))}
              </ol>
            </div>
            <ClanChatPanel email={email} clanId={me.clan_id} open={open} />
            <button
              data-testid="clan-leave-button"
              disabled={busy}
              onClick={leave}
              className="w-full text-[10px] tracking-[0.3em] py-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 disabled:opacity-40 flex items-center justify-center gap-2"
            >
              <LogOut size={12} /> LEAVE CLAN
            </button>
          </div>
        )}

        {me && !me.in_clan && email && (
          <div className="space-y-5">
            <div className="border border-[#39FF14]/40 p-4 bg-[#39FF14]/5">
              <div className="text-[10px] tracking-[0.4em] text-[#39FF14] mb-3">FOUND A NEW CLAN</div>
              <div className="grid grid-cols-3 gap-2">
                <Input
                  data-testid="clan-create-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Clan name"
                  className="col-span-2 bg-black/60 border-white/20 text-white text-sm"
                  maxLength={20}
                />
                <Input
                  data-testid="clan-create-tag"
                  value={tag}
                  onChange={(e) => setTag(e.target.value.toUpperCase())}
                  placeholder="TAG"
                  className="bg-black/60 border-white/20 text-white text-sm uppercase"
                  maxLength={5}
                />
              </div>
              <button
                data-testid="clan-create-button"
                disabled={busy || name.trim().length < 3 || tag.trim().length < 2}
                onClick={create}
                className="mt-3 w-full text-[10px] tracking-[0.3em] py-2 border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black disabled:opacity-40 flex items-center justify-center gap-2"
              >
                <Plus size={12} /> CREATE CLAN
              </button>
            </div>

            <div className="border border-white/10 p-4">
              <div className="text-[10px] tracking-[0.4em] text-white/50 mb-3">OPEN CLANS · JOIN ONE</div>
              {openClans.length === 0 ? (
                <div className="text-xs text-white/40 text-center py-3">NO OPEN CLANS · CREATE THE FIRST</div>
              ) : (
                <ul className="space-y-2">
                  {openClans.map((c, i) => (
                    <li
                      key={c.clan_id}
                      data-testid={`clan-open-${i}`}
                      className="flex items-center justify-between text-xs"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] tracking-[0.3em] px-2 py-0.5 border border-white/30 text-white/70">
                          [{c.tag}]
                        </span>
                        <span className="text-white/80 tracking-wider">{c.name}</span>
                        <span className="text-white/40 text-[10px] tabular-nums">
                          {c.member_count}/{c.member_max}
                        </span>
                      </div>
                      <button
                        data-testid={`clan-join-${i}`}
                        disabled={busy}
                        onClick={() => join(c.clan_id, `[${c.tag}] ${c.name}`)}
                        className="text-[10px] tracking-[0.3em] px-3 py-1 border border-[#F0ABFC] text-[#F0ABFC] hover:bg-[#F0ABFC] hover:text-black disabled:opacity-40"
                      >
                        JOIN →
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ClanChatPanel({ email, clanId, open }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  const fetchList = useCallback(() => {
    if (!email) return;
    axios
      .get(`${API}/clan/chat/list`, { params: { email, limit: 50 } })
      .then((r) => {
        const msgs = r.data?.messages || [];
        setMessages(msgs);
      })
      .catch(() => {});
  }, [email]);

  // Initial load + polling
  useEffect(() => {
    if (!open || !clanId) return;
    fetchList();
    pollRef.current = setInterval(fetchList, 5000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [open, clanId, fetchList]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const send = async () => {
    const clean = text.trim();
    if (!clean) return;
    if (clean.length > 200) {
      toast.error("Message too long · max 200 chars");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/clan/chat/send`, { email, text: clean });
      setText("");
      fetchList();
    } catch (e) {
      const status = e?.response?.status;
      const msg =
        status === 429
          ? "SLOW DOWN · one message every 3 seconds"
          : e?.response?.data?.detail || "Send failed";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const fmtTime = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "";
    }
  };

  return (
    <div className="border border-white/10 p-3 bg-black/30" data-testid="clan-chat-panel">
      <div className="text-[10px] tracking-[0.4em] text-white/50 mb-2">CLAN CHAT</div>
      <div
        ref={scrollRef}
        data-testid="clan-chat-scroll"
        className="max-h-40 overflow-y-auto space-y-1 mb-2 pr-1"
      >
        {messages.length === 0 ? (
          <div className="text-[10px] text-white/40 tracking-[0.2em] text-center py-3">
            NO MESSAGES YET · SAY HI
          </div>
        ) : (
          messages.map((m, i) => (
            <div
              key={m.id || i}
              data-testid={`clan-chat-msg-${i}`}
              className="text-xs flex items-baseline gap-2 border-b border-white/5 pb-1"
            >
              <span
                className={m.gem === "prismatic" ? "prismatic-text" : "text-white/70"}
                style={
                  m.gem && m.gem !== "prismatic"
                    ? {
                        color:
                          m.gem === "crown"
                            ? "#FFD400"
                            : m.gem === "fire"
                            ? "#FF6030"
                            : m.gem === "star"
                            ? "#F0ABFC"
                            : "#FFF",
                      }
                    : {}
                }
              >
                {m.handle}
              </span>
              <span className="text-white/90 flex-1 break-words">{m.text}</span>
              <span className="text-[9px] text-white/30 tabular-nums shrink-0">{fmtTime(m.created_at)}</span>
            </div>
          ))
        )}
      </div>
      <div className="flex gap-2">
        <Input
          data-testid="clan-chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          placeholder="Type a message…"
          maxLength={200}
          className="bg-black/60 border-white/20 text-white text-sm"
        />
        <button
          data-testid="clan-chat-send"
          disabled={sending || !text.trim()}
          onClick={send}
          className="px-3 border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black disabled:opacity-40 flex items-center gap-1 text-[10px] tracking-[0.3em]"
        >
          <Send size={12} />
        </button>
      </div>
    </div>
  );
}
